import type { ProblemJSDependencies } from '../problemJSDependencies';
import {
  getReadAloudWrapper,
  injectReadAloudButton,
} from '../readAloud/readAloudUtil';
import type { TTSDTO } from '../readAloud/TTS';
import { BasicObject, getBasicObject } from '../uiObjects/basicObject';

import { Line } from './Line';

export interface SystemDTO {
  linesXML: any;
  tts?: TTSDTO;
}

export function buildSystem(
  problemJS: ProblemJSDependencies,
  args: SystemDTO,
  targetForReadAloud?: BasicObject,
  x = 0,
  y = 0
): BasicObject {
  const system = new System(problemJS, args, x, y);

  if (problemJS.isReadAloud() && args.tts) {
    const readAloudButtonDependencies = {
      paintCanvas: problemJS.paintCanvas,
      errorToServer: problemJS.errorToServer,
      $canvas: problemJS.$canvas,
    };

    if (targetForReadAloud) {
      injectReadAloudButton(
        targetForReadAloud,
        args.tts,
        readAloudButtonDependencies,
        system
      );

      return system;
    }

    const wrapper = getReadAloudWrapper();

    wrapper.add(system);
    const readAloudButton = injectReadAloudButton(
      wrapper,
      args.tts,
      readAloudButtonDependencies,
      system
    );

    if (!readAloudButton) return system;

    wrapper.layoutChildrenLR();
    wrapper.sizeMeToLowestAndRightmostChildren();

    return wrapper;
  }

  return system;
}

export class System extends BasicObject {
  lines: Line[];

  constructor(problemJS: ProblemJSDependencies, args: SystemDTO, x = 0, y = 0) {
    super(x, y);

    this.lines = [];

    let yCoor = 0;

    for (let a = 0; a < args.linesXML.length; a++) {
      const line = problemJS.getStaticPanel(args.linesXML[a]) as Line;

      line.viewportY = yCoor;
      yCoor += line.viewportH;
      this.lines.push(line);
      this.add(line);

      if (a !== args.linesXML.length - 1) {
        const spacer = getBasicObject();

        spacer.setAllDim(1, 5);
        spacer.viewportY = yCoor;
        yCoor += 5;
        this.add(spacer);
      }
    }

    this.alignMiddleSymbols();
    this.sizeMeToFitChildren();
  }

  alignMiddleSymbols() {
    if (this.lines.length === 0) return;

    const maxPixelsToMiddle = this.pixelsToMiddleSymbol();

    this.lines.forEach(line => {
      const m = line.pixelsToMiddleSymbol();
      const delta = maxPixelsToMiddle - m;

      if (delta > 0) {
        line.viewportX = delta;
      }
    });
  }

  pixelsToMiddleSymbol(): number {
    if (this.lines.length === 0) return 0;

    return Math.max(...this.lines.map(line => line.pixelsToMiddleSymbol()));
  }

  isBaselineAware(): boolean {
    return true;
  }

  getVertToBaseline(): number {
    return this.lines.length === 0 ? 0 : this.lines[0].getVertToBaseline();
  }
}
